import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';

const { FiSearch, FiMessageSquare, FiPhone, FiTrash2, FiCalendar, FiPlus, FiX, FiCheck, FiMail, FiTruck, FiChevronDown, FiChevronUp, FiEdit2, FiAlertCircle, FiSettings } = FiIcons;

const Subscribers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [notification, setNotification] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const navigate = useNavigate();

  // Form State
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    company: '',
    email: '',
    phone: '',
    status: 'Active',
    preferences: {
      sms: true,
      email: true
    }
  });

  const [formTrucks, setFormTrucks] = useState([
    { id: Date.now(), make: '', model: '', year: '', vin: '', plate: '', nextTest: '' }
  ]);

  // Load Data
  useEffect(() => {
    const storedSubs = localStorage.getItem('ramirez_subscribers');
    if (storedSubs) {
      setSubscribers(JSON.parse(storedSubs));
    } else {
      const defaultSubs = [
        { 
          id: 1, 
          name: "Carlos Rivera", 
          company: "Rivera Transport", 
          email: "carlos@riveratrans.com", 
          phone: "9095550101", 
          fleetSize: "2 Trucks", 
          nextInspection: "2024-04-15", 
          status: "Active",
          preferences: { sms: true, email: true },
          trucks: [
            { id: 101, make: 'Peterbilt', model: '389', year: '2018', vin: '1XP5DB9X...', plate: '8P99881', nextTest: '2024-04-15' },
            { id: 102, make: 'Kenworth', model: 'W900', year: '2020', vin: '1NKDX4X...', plate: '6T11223', nextTest: '2024-06-01' }
          ]
        },
        { 
          id: 2, 
          name: "Sarah Smith", 
          company: "Smith Logistics", 
          email: "sarah@smithlog.com", 
          phone: "9095550123", 
          fleetSize: "1 Truck", 
          nextInspection: "2024-03-28", 
          status: "Due Soon",
          preferences: { sms: true, email: false },
          trucks: [
            { id: 201, make: 'Freightliner', model: 'Cascadia', year: '2016', vin: '3AKJGKD...', plate: '4W55667', nextTest: '2024-03-28' }
          ]
        }
      ];
      setSubscribers(defaultSubs);
      localStorage.setItem('ramirez_subscribers', JSON.stringify(defaultSubs));
    }
  }, []);

  // Save Data
  useEffect(() => {
    if (subscribers.length >= 0) {
      localStorage.setItem('ramirez_subscribers', JSON.stringify(subscribers));
    }
  }, [subscribers]);

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  // --- Modal & Form Handlers ---

  const openAddModal = () => {
    setIsEditing(false);
    setFormData({ 
      id: null, 
      name: '', 
      company: '', 
      email: '', 
      phone: '', 
      status: 'Active',
      preferences: { sms: true, email: true }
    });
    setFormTrucks([{ id: Date.now(), make: '', model: '', year: '', vin: '', plate: '', nextTest: '' }]);
    setIsModalOpen(true);
  };

  const openEditModal = (sub) => {
    setIsEditing(true);
    setFormData({
      id: sub.id,
      name: sub.name,
      company: sub.company,
      email: sub.email || '',
      phone: sub.phone,
      status: sub.status,
      preferences: sub.preferences || { sms: true, email: true }
    });
    // Ensure trucks have IDs
    const trucksWithIds = (sub.trucks || []).map(t => ({ 
      ...t, 
      id: t.id || Date.now() + Math.random(),
      vin: t.vin || '',
      plate: t.plate || ''
    }));
    setFormTrucks(trucksWithIds.length > 0 ? trucksWithIds : [{ id: Date.now(), make: '', model: '', year: '', vin: '', plate: '', nextTest: '' }]);
    setIsModalOpen(true);
  };

  const handleSaveSubscriber = (e) => {
    e.preventDefault();
    
    // Calculate summary fields
    const dates = formTrucks.map(t => t.nextTest).filter(d => d).sort();
    const nextInspection = dates.length > 0 ? dates[0] : '';
    const fleetSize = `${formTrucks.length} Truck${formTrucks.length !== 1 ? 's' : ''}`;

    const subscriberData = {
      ...formData,
      trucks: formTrucks,
      fleetSize,
      nextInspection
    };

    if (isEditing) {
      setSubscribers(subscribers.map(sub => sub.id === formData.id ? { ...subscriberData, id: formData.id } : sub));
      showNotification("Subscriber updated successfully!");
    } else {
      setSubscribers([{ ...subscriberData, id: Date.now() }, ...subscribers]);
      showNotification("New subscriber added!");
    }
    
    setIsModalOpen(false);
  };

  const handleAddTruckRow = () => {
    setFormTrucks([...formTrucks, { id: Date.now(), make: '', model: '', year: '', vin: '', plate: '', nextTest: '' }]);
  };

  const handleRemoveTruckRow = (id) => {
    if (formTrucks.length > 1) {
      setFormTrucks(formTrucks.filter(t => t.id !== id));
    }
  };

  const handleTruckInput = (id, field, value) => {
    setFormTrucks(formTrucks.map(t => t.id === id ? { ...t, [field]: value } : t));
  };
  
  const handlePreferenceChange = (type) => {
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [type]: !prev.preferences[type]
      }
    }));
  };

  // --- Actions ---

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to remove this subscriber?")) {
      setSubscribers(subscribers.filter(sub => sub.id !== id));
      showNotification("Subscriber removed.");
    }
  };

  const handleBlastSMS = () => {
    navigate('/admin/sms');
  };

  const sendTargetedMessage = (sub, truck = null, type = 'sms') => {
    // Check preferences first
    if (type === 'sms' && sub.preferences && !sub.preferences.sms) {
      if (!window.confirm(`${sub.company} has opted OUT of SMS. Send anyway?`)) return;
    }
    if (type === 'email' && sub.preferences && !sub.preferences.email) {
      if (!window.confirm(`${sub.company} has opted OUT of Email. Send anyway?`)) return;
    }

    let message = '';
    let recipient = type === 'sms' ? sub.phone : sub.email;

    if (truck) {
      message = `Reminder: Your ${truck.year} ${truck.make} ${truck.model} (VIN: ...${truck.vin.slice(-4)}) is due for CARB Clean Truck Check on ${truck.nextTest}. Call Ramirez Mobile to schedule.`;
    } else {
      message = `Hello ${sub.name}, this is a reminder from Ramirez Mobile regarding your fleet compliance. Please contact us.`;
    }

    if (type === 'sms') {
      navigate('/admin/sms', { state: { phone: recipient, message, audience: 'single' } });
    } else {
      navigate('/admin/email', { state: { email: recipient, subject: 'Compliance Reminder', message, audience: 'single' } });
    }
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-900/30 text-green-400 border-green-800';
      case 'Due Soon': return 'bg-yellow-900/30 text-yellow-400 border-yellow-800';
      case 'Urgent': return 'bg-red-900/30 text-red-400 border-red-800';
      default: return 'bg-gray-700 text-gray-300';
    }
  };

  const filteredSubscribers = subscribers.filter(sub =>
    sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (sub.email && sub.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Fleet Management</h1>
          <p className="text-gray-400">Manage subscribers, update vehicle lists, and set notification preferences.</p>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-gray-500" />
            <input
              type="text"
              placeholder="Search subscribers..."
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={openAddModal}
            className="bg-green-600 text-white px-4 py-2.5 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap shadow-lg shadow-green-900/20"
          >
            <SafeIcon icon={FiPlus} />
            <span>Add Client</span>
          </button>
          <button onClick={handleBlastSMS} className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap">
            <SafeIcon icon={FiMessageSquare} />
            <span>Blast SMS</span>
          </button>
        </div>
      </div>

      {notification && (
        <div className="fixed top-20 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-fade-in-up">
          <SafeIcon icon={FiCheck} />
          {notification}
        </div>
      )}

      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-900/50 border-b border-gray-700 text-gray-400 text-sm uppercase">
                <th className="px-6 py-4 font-medium">Subscriber</th>
                <th className="px-6 py-4 font-medium">Contact</th>
                <th className="px-6 py-4 font-medium">Prefs</th>
                <th className="px-6 py-4 font-medium">Next Due</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredSubscribers.length > 0 ? (
                filteredSubscribers.map((sub) => (
                  <React.Fragment key={sub.id}>
                    <tr className={`hover:bg-gray-700/30 transition-colors group ${expandedRow === sub.id ? 'bg-gray-700/40' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="font-bold text-white">{sub.company}</div>
                        <div className="text-sm text-gray-400">{sub.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 text-gray-300">
                            <SafeIcon icon={FiPhone} className="text-gray-500 text-xs" />
                            <span className="text-sm">{sub.phone}</span>
                          </div>
                          {sub.email && (
                            <div className="flex items-center gap-2 text-gray-400">
                              <SafeIcon icon={FiMail} className="text-gray-500 text-xs" />
                              <span className="text-xs">{sub.email}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                           <span className={`text-xs px-2 py-0.5 rounded border ${sub.preferences?.sms ? 'bg-blue-900/30 text-blue-400 border-blue-800' : 'bg-gray-800 text-gray-500 border-gray-700 opacity-50'}`}>SMS</span>
                           <span className={`text-xs px-2 py-0.5 rounded border ${sub.preferences?.email ? 'bg-purple-900/30 text-purple-400 border-purple-800' : 'bg-gray-800 text-gray-500 border-gray-700 opacity-50'}`}>Email</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-gray-300">
                          <SafeIcon icon={FiCalendar} className="text-gray-500" />
                          {sub.nextInspection || 'See Details'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(sub.status)}`}>
                          {sub.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => toggleRow(sub.id)}
                            className={`p-2 rounded transition-colors ${expandedRow === sub.id ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}
                            title="View Fleet"
                          >
                            <SafeIcon icon={expandedRow === sub.id ? FiChevronUp : FiChevronDown} />
                          </button>
                          <button 
                            onClick={() => openEditModal(sub)}
                            className="p-2 bg-yellow-600/20 text-yellow-400 rounded hover:bg-yellow-600 hover:text-white transition-colors"
                            title="Edit Fleet"
                          >
                            <SafeIcon icon={FiEdit2} />
                          </button>
                          <button 
                            onClick={() => sendTargetedMessage(sub, null, 'sms')}
                            className="p-2 bg-blue-600/20 text-blue-400 rounded hover:bg-blue-600 hover:text-white transition-colors"
                            title="Send General SMS"
                          >
                            <SafeIcon icon={FiMessageSquare} />
                          </button>
                          <button 
                            onClick={() => handleDelete(sub.id)}
                            className="p-2 bg-red-600/20 text-red-400 rounded hover:bg-red-600 hover:text-white transition-colors"
                            title="Remove"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRow === sub.id && (
                      <tr className="bg-gray-900/30">
                        <td colSpan="6" className="px-6 py-4 border-b border-gray-700 shadow-inner">
                          <div className="pl-4 border-l-2 border-blue-500">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="text-sm font-bold text-gray-300 flex items-center gap-2">
                                <SafeIcon icon={FiTruck} /> Fleet Vehicles ({sub.trucks?.length || 0})
                              </h4>
                              <button onClick={() => openEditModal(sub)} className="text-xs text-blue-400 hover:underline">
                                + Add Truck
                              </button>
                            </div>
                            
                            {sub.trucks && sub.trucks.length > 0 ? (
                              <div className="grid grid-cols-1 gap-3">
                                {sub.trucks.map((truck, idx) => (
                                  <div key={idx} className="bg-gray-800 p-3 rounded-lg border border-gray-700 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                    <div className="flex-1">
                                      <div className="font-bold text-white text-sm">{truck.year} {truck.make} {truck.model}</div>
                                      <div className="flex gap-4 mt-1">
                                        {truck.vin && (
                                          <div className="text-gray-500 text-xs bg-gray-900/50 px-2 py-0.5 rounded border border-gray-700 font-mono">
                                            VIN: {truck.vin}
                                          </div>
                                        )}
                                        {truck.plate && (
                                          <div className="text-gray-500 text-xs bg-gray-900/50 px-2 py-0.5 rounded border border-gray-700 font-mono">
                                            Plate: {truck.plate}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end">
                                      <div className="text-right">
                                        <div className="text-xs text-gray-400">Next Test Due</div>
                                        <div className="text-sm font-medium text-blue-400">{truck.nextTest || 'Not Set'}</div>
                                      </div>
                                      <div className="flex gap-2">
                                        <button 
                                          onClick={() => sendTargetedMessage(sub, truck, 'sms')}
                                          className="text-xs bg-gray-700 hover:bg-blue-600 text-white px-3 py-1.5 rounded flex items-center gap-1 transition-colors"
                                          title="Send Reminder SMS for this truck"
                                        >
                                          <SafeIcon icon={FiMessageSquare} /> SMS
                                        </button>
                                        <button 
                                          onClick={() => sendTargetedMessage(sub, truck, 'email')}
                                          className="text-xs bg-gray-700 hover:bg-purple-600 text-white px-3 py-1.5 rounded flex items-center gap-1 transition-colors"
                                          title="Send Reminder Email for this truck"
                                        >
                                          <SafeIcon icon={FiMail} /> Email
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-gray-500 text-sm italic flex items-center gap-2">
                                <SafeIcon icon={FiAlertCircle} /> No vehicle data. Click edit to add trucks.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No subscribers found. Add one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="bg-gray-900/30 px-6 py-4 border-t border-gray-700 flex justify-between items-center text-sm text-gray-400">
          <span>Showing {filteredSubscribers.length} subscribers</span>
        </div>
      </div>

      {/* Add/Edit Subscriber Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl border border-gray-700 overflow-hidden animate-fade-in-up max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center bg-gray-900/50">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <SafeIcon icon={isEditing ? FiEdit2 : FiPlus} className="text-blue-500" />
                {isEditing ? 'Edit Subscriber & Fleet' : 'Add New Subscriber'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
                type="button"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>
            
            <form onSubmit={handleSaveSubscriber} className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Contact Info */}
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-gray-700 pb-2">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Client Information</h3>
                  {/* Notification Toggles */}
                  <div className="flex gap-4">
                     <label className="flex items-center gap-2 cursor-pointer">
                        <div className={`w-8 h-4 rounded-full relative transition-colors ${formData.preferences?.sms ? 'bg-blue-600' : 'bg-gray-600'}`} onClick={() => handlePreferenceChange('sms')}>
                           <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${formData.preferences?.sms ? 'translate-x-4' : 'translate-x-0'}`}></div>
                        </div>
                        <span className="text-xs text-gray-300">Receive SMS</span>
                     </label>
                     <label className="flex items-center gap-2 cursor-pointer">
                        <div className={`w-8 h-4 rounded-full relative transition-colors ${formData.preferences?.email ? 'bg-purple-600' : 'bg-gray-600'}`} onClick={() => handlePreferenceChange('email')}>
                           <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${formData.preferences?.email ? 'translate-x-4' : 'translate-x-0'}`}></div>
                        </div>
                        <span className="text-xs text-gray-300">Receive Email</span>
                     </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm font-medium mb-1">Company Name</label>
                    <input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm font-medium mb-1">Contact Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm font-medium mb-1">Phone</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                     <label className="block text-gray-400 text-sm font-medium mb-1">Status</label>
                     <select 
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                     >
                        <option value="Active">Active</option>
                        <option value="Due Soon">Due Soon</option>
                        <option value="Urgent">Urgent</option>
                        <option value="Inactive">Inactive</option>
                     </select>
                  </div>
                </div>
              </div>

              {/* Truck Info */}
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-gray-700 pb-2">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Vehicle Details</h3>
                  <button type="button" onClick={handleAddTruckRow} className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 flex items-center gap-1">
                    <SafeIcon icon={FiPlus} /> Add Truck
                  </button>
                </div>
                
                {formTrucks.map((truck, idx) => (
                  <div key={truck.id} className="bg-gray-700/30 p-3 rounded-lg border border-gray-600 relative group">
                    {formTrucks.length > 1 && (
                      <button type="button" onClick={() => handleRemoveTruckRow(truck.id)} className="absolute top-2 right-2 text-gray-500 hover:text-red-400 z-10">
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                      </button>
                    )}
                    <span className="text-xs text-gray-500 block mb-2">Truck #{idx + 1}</span>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                      <div className="md:col-span-1">
                        <label className="text-[10px] text-gray-400 block mb-1">Year</label>
                        <input
                          type="number"
                          placeholder="2020"
                          className="w-full bg-gray-800 border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none"
                          value={truck.year}
                          onChange={(e) => handleTruckInput(truck.id, 'year', e.target.value)}
                        />
                      </div>
                      <div className="md:col-span-1">
                        <label className="text-[10px] text-gray-400 block mb-1">Make</label>
                        <input
                          type="text"
                          placeholder="Make"
                          className="w-full bg-gray-800 border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none"
                          value={truck.make}
                          onChange={(e) => handleTruckInput(truck.id, 'make', e.target.value)}
                        />
                      </div>
                      <div className="md:col-span-1">
                        <label className="text-[10px] text-gray-400 block mb-1">Model</label>
                        <input
                          type="text"
                          placeholder="Model"
                          className="w-full bg-gray-800 border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none"
                          value={truck.model}
                          onChange={(e) => handleTruckInput(truck.id, 'model', e.target.value)}
                        />
                      </div>
                      <div className="md:col-span-1">
                         <label className="text-[10px] text-gray-400 block mb-1">VIN #</label>
                         <input
                           type="text"
                           placeholder="VIN"
                           className="w-full bg-gray-800 border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none font-mono"
                           value={truck.vin}
                           onChange={(e) => handleTruckInput(truck.id, 'vin', e.target.value)}
                         />
                      </div>
                      <div className="md:col-span-1">
                         <label className="text-[10px] text-gray-400 block mb-1">License Plate</label>
                         <input
                           type="text"
                           placeholder="Plate"
                           className="w-full bg-gray-800 border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none font-mono"
                           value={truck.plate}
                           onChange={(e) => handleTruckInput(truck.id, 'plate', e.target.value)}
                         />
                      </div>
                      <div className="md:col-span-1">
                        <label className="text-[10px] text-blue-400 font-bold block mb-1">Next Test Date</label>
                        <input
                          type="date"
                          className="w-full bg-gray-800 border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none"
                          value={truck.nextTest}
                          onChange={(e) => handleTruckInput(truck.id, 'nextTest', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </form>

            <div className="p-6 pt-4 border-t border-gray-700 bg-gray-800 flex gap-3">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSubscriber}
                type="button"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiCheck} />
                {isEditing ? 'Update Subscriber' : 'Add Subscriber'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Subscribers;